import { useTranslation } from 'react-i18next';

import type { PasoAyuda, PreguntaAyuda } from './pasos';

function uno(selector: string): Element[] {
  const el = document.querySelector(selector);
  return el ? [el] : [];
}

function todos(selector: string): Element[] {
  return [...document.querySelectorAll(selector)];
}

/** ¿Sale la fila `id` en Ajustes? (Algunas dependen de la cuenta o el permiso.) */
function hayAjuste(id: string): boolean {
  return !!document.querySelector(`[data-testid="ajuste-${id}"]`);
}

const fila = (id: string) => uno(`[data-testid="ajuste-${id}"]`);
const contenido = (id: string) => uno(`[data-testid="ajuste-${id}-contenido"]`);

/**
 * La ayuda de Ajustes (maqueta aprobada por Vadik el 27-oct-2026, bromas B).
 * Ajustes es larga: el recorrido va por grupos y solo despliega las filas que
 * no se entienden desde fuera (Amarre, Notificaciones y Copia). Gavi abre y
 * cierra filas, pero nunca pulsa nada de dentro: ni «Subir a la nube», ni
 * «Crear otra llave», ni «Cerrar sesión», ni «Borrar». Nada de banco.
 */
export function usePreguntasAjustes(): PreguntaAyuda[] {
  const { t } = useTranslation();

  const todo: PasoAyuda[] = [
    {
      id: 'hola',
      escena: 'normal',
      radio: 22,
      texto: t(
        'Hi! These are your Settings: how your budget is kept, how I let you know things and how Rumbo looks. It is the screen with the most buttons in Rumbo and none of them charges you for pressing it. Banks, take note.',
      ),
      objetivo: () => uno('[data-testid="presupuesto-abierto"]'),
    },
    {
      id: 'cambiar',
      escena: 'normal',
      radio: 22,
      texto: t(
        'At the top, the budget you have open. "Switch" does not change its name: it takes you to the list to open another one. The name is changed in the "Name" row.',
      ),
      objetivo: () => uno('[data-testid="presupuesto-cambiar"]'),
    },
    {
      id: 'presupuesto',
      escena: 'normal',
      radio: 18,
      texto: t(
        '"Budget" is about this budget: its name, where it is kept, the tutorial ("First voyage"), how I talk to you, my notifications and your payroll.',
      ),
      objetivo: () => uno('[data-testid="grupo-presupuesto"]'),
    },
    {
      id: 'amarre',
      escena: 'ajuste-amarre',
      disponible: () => hayAjuste('amarre'),
      radio: 16,
      texto: t(
        '"Mooring" is where your budget is kept: on this phone and in the cloud, encrypted. You see it on your other devices and you do not lose it if you change phones.',
      ),
      objetivo: () => [...fila('amarre'), ...contenido('amarre')],
    },
    {
      id: 'notificaciones',
      escena: 'ajuste-notificaciones',
      radio: 16,
      texto: t(
        'In "Notifications" you decide when I give you a nudge (a notice on your phone), what I remind you of and in what tone. At the end, "Send me a test one" shows you how they arrive.',
      ),
      objetivo: () => [
        ...fila('notificaciones'),
        ...uno('[data-testid="notif-activo"]'),
      ],
    },
    {
      id: 'datos',
      escena: 'ajuste-copia',
      radio: 18,
      texto: t(
        '"Your data" is your safety net: "Backup" keeps a file with everything and here you see when the last one was. Further down, whether you help improve Rumbo, "Something is wrong" to write to us and, if you have an account, deleting it: that cannot be undone.',
      ),
      objetivo: () => uno('[data-testid="grupo-datos"]'),
    },
    {
      id: 'apariencia',
      escena: 'normal',
      radio: 18,
      texto: t(
        '"Appearance" is how you see Rumbo: light or dark theme, text size, number and date format, and language.',
      ),
      objetivo: () => uno('[data-testid="grupo-apariencia"]'),
    },
    {
      id: 'cuenta',
      escena: 'normal',
      disponible: () => hayAjuste('repuesto'),
      radio: 18,
      texto: t(
        '"Account": signing in with your fingerprint, your "Spare key" (the code that opens your data if you forget the password) and signing out.',
      ),
      objetivo: () => uno('[data-testid="grupo-cuenta"]'),
    },
    {
      id: 'herramientas',
      escena: 'normal',
      radio: 14,
      texto: t(
        '"Tools" is the workshop: things to fix the app if it ever fails. Leave it alone unless we ask you to.',
      ),
      objetivo: () => fila('herramientas'),
    },
    {
      id: 'final',
      escena: 'normal',
      final: true,
      radio: 24,
      texto: t(
        'Done! If you forget something, tap the porthole and I will tell you again. Do not ask me about the cache: to me it sounds like something you eat.',
      ),
      objetivo: () => uno('[data-testid="ojo-de-buey"]'),
    },
  ];

  const movil: PasoAyuda[] = [
    {
      id: 'm-amarre',
      escena: 'ajuste-amarre',
      disponible: () => hayAjuste('amarre'),
      radio: 16,
      texto: t(
        'Your budget is in the cloud ("Mooring" says "Phone and cloud"): on the new phone you sign in with your account and your Rumbo password and it appears by itself.',
      ),
      objetivo: () => [...fila('amarre'), ...contenido('amarre')],
    },
    {
      id: 'm-copia',
      escena: 'ajuste-copia',
      radio: 12,
      texto: t(
        'Just in case, "Make a backup now" and keep it off the phone, in Drive for example. With that file you get everything back on any phone.',
      ),
      objetivo: () => uno('[data-testid="copia-ahora"]'),
    },
    {
      id: 'm-llave',
      escena: 'normal',
      disponible: () => hayAjuste('repuesto'),
      radio: 12,
      texto: t(
        'And keep your spare key at hand: if on the new phone you do not remember your Rumbo password, it is what opens your data.',
      ),
      objetivo: () => fila('repuesto'),
    },
    {
      id: 'm-huella',
      escena: 'normal',
      disponible: () => hayAjuste('huella'),
      radio: 12,
      texto: t(
        'Lost it and it signed in with your fingerprint? In "Fingerprint" › "Other devices" you remove it, and that phone no longer signs in with your fingerprint.',
      ),
      objetivo: () => fila('huella'),
    },
  ];

  const llave: PasoAyuda[] = [
    {
      id: 'k-que',
      escena: 'ajuste-repuesto',
      disponible: () => hayAjuste('repuesto'),
      radio: 14,
      texto: t(
        'The spare key is a code that opens your data if you forget your Rumbo password. Only you have it: not even Rumbo keeps it.',
      ),
      objetivo: () => contenido('repuesto'),
    },
    {
      id: 'k-crear',
      escena: 'ajuste-repuesto',
      disponible: () => hayAjuste('repuesto'),
      radio: 16,
      texto: t(
        'If you lost it or never kept it, "Create a new spare key" gives you a new one and the old one stops working. Keep it on paper, as a PDF or in a password manager.',
      ),
      objetivo: () => uno('[data-testid="repuesto-crear"]'),
    },
    {
      id: 'k-olvidada',
      escena: 'normal',
      disponible: () => hayAjuste('repuesto'),
      radio: 12,
      texto: t(
        'If you have already forgotten it: when you open Rumbo, in "Open your vault", tap "I don\'t remember it", paste your key and choose a new password. You lose nothing.',
      ),
      objetivo: () => fila('repuesto'),
    },
  ];

  const copia: PasoAyuda[] = [
    {
      id: 'c-que',
      escena: 'ajuste-copia',
      radio: 14,
      texto: t(
        '"Backup" is a file with all your data. Choose where and how often, and it makes itself; here you see when the last one was.',
      ),
      objetivo: () => uno('[data-testid="copia-ajustes"]'),
    },
    {
      id: 'c-ahora',
      escena: 'ajuste-copia',
      radio: 12,
      texto: t(
        '"Make a backup now" lets you choose where to keep it: Drive or this phone. It carries all your transactions, so somewhere private.',
      ),
      objetivo: () => uno('[data-testid="copia-ahora"]'),
    },
    {
      id: 'c-recuperar',
      escena: 'normal',
      radio: 22,
      texto: t(
        'To get it back: "Switch", at the top, then "Import file" › "Budget file (.zip)".',
      ),
      objetivo: () => uno('[data-testid="presupuesto-cambiar"]'),
    },
  ];

  // Solo quien lleva el mando invita; quien va a bordo o solo mira no tiene
  // nada que hacer aquí.
  const puedeInvitar = () =>
    !!document.querySelector('[data-testid="ver-tripulacion"][data-al-mando]');
  const pareja: PasoAyuda[] = [
    {
      id: 'p-quien',
      escena: 'ajuste-compartir',
      disponible: () => hayAjuste('compartir'),
      radio: 16,
      texto: t(
        'In "Share" you choose who else opens this budget: "View and edit" (they change it like you) or "View only" (they look, but nothing is saved).',
      ),
      objetivo: () => (puedeInvitar() ? contenido('compartir') : []),
    },
    {
      id: 'p-enlace',
      escena: 'ajuste-compartir',
      disponible: () => hayAjuste('compartir'),
      radio: 16,
      texto: t(
        'In "See the crew", "Invite someone" creates a link. Send it however you like: they open it, sign in with Google and land in this same budget. It works for 7 days and for one person.',
      ),
      objetivo: () =>
        puedeInvitar() ? uno('[data-testid="ver-tripulacion"]') : [],
    },
    {
      id: 'p-tripulacion',
      escena: 'ajuste-compartir',
      disponible: () => hayAjuste('compartir'),
      radio: 14,
      texto: t(
        'In "See the crew" each person is on one line. With "…" you change their permission or take them off the boat; what they noted stays in the plan.',
      ),
      objetivo: () =>
        puedeInvitar() ? uno('[data-testid="ver-tripulacion"]') : [],
    },
  ];

  const avisos: PasoAyuda[] = [
    {
      id: 'a-interruptor',
      escena: 'ajuste-notificaciones',
      radio: 16,
      texto: t(
        'First, "Notifications on this phone" has to be on. If below it says your phone blocks them, turn them on in your phone settings: Apps › Rumbo › Notifications.',
      ),
      objetivo: () => uno('[data-testid="notif-activo"]'),
    },
    {
      id: 'a-ritmo',
      escena: 'ajuste-notificaciones',
      radio: 14,
      texto: t(
        'Then "How often": the time and days you mark are when I write to you. During "Quiet hours" I do not bother you even if it is time.',
      ),
      objetivo: () => todos('[data-testid^="notif-ritmo-"]'),
    },
    {
      id: 'a-prueba',
      escena: 'ajuste-notificaciones',
      radio: 16,
      texto: t(
        '"Send me a test one" and look at the top of the screen. If it does not show, it is the phone stopping it: check battery saving and Do Not Disturb.',
      ),
      objetivo: () => uno('[data-testid="notif-prueba"]'),
    },
  ];

  const vista: PasoAyuda[] = [
    {
      id: 'v-tema',
      escena: 'ajuste-tema',
      radio: 16,
      texto: t(
        '"Theme": "Automatic" follows your phone; "Light" or "Dark", whatever you say. It changes right away.',
      ),
      objetivo: () => [...fila('tema'), ...contenido('tema')],
    },
    {
      id: 'v-tamano',
      escena: 'ajuste-tamano',
      radio: 14,
      texto: t(
        '"Size" makes text, margins and rows bigger or smaller at once; the sample shows you. Choose and press "Apply".',
      ),
      objetivo: () =>
        todos(
          '[data-testid="escala-selector"], [data-testid="escala-muestra"]',
        ),
    },
  ];

  return [
    { id: 'todo', texto: t('Tell me about this screen'), pasos: todo },
    {
      id: 'movil',
      texto: t('I am changing phones (or lost it): do I lose my data?'),
      pasos: movil,
    },
    {
      id: 'llave',
      texto: t('I forgot the password (what is the spare key?)'),
      pasos: llave,
    },
    { id: 'copia', texto: t('How do I make a backup?'), pasos: copia },
    {
      id: 'pareja',
      texto: t('How do I share the budget with my partner?'),
      pasos: pareja,
    },
    {
      id: 'avisos',
      texto: t('Why are my notifications not arriving?'),
      pasos: avisos,
    },
    {
      id: 'vista',
      texto: t('Can I use dark mode or bigger text?'),
      pasos: vista,
    },
  ];
}
